import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment.development';
import { ErrorHandlingService } from '../../../../core/services/error-handling.service';
import { ApiResponse } from '../../../../core/models/api-response.models';
import {
  AddComment,
  BehaviorGraphDataResponse,
  BehaviorMultiGraphDataResponse,
  BIPGraphPayload,
  BlockValueModel,
  DataNote,
  DataSheetQueryParams,
  DataSheetResult,
  EditComment,
  enableDatasheet,
  GraphList,
  InsightData,
  Unit,
  UnitMultiValue,
} from './insights-data.interface';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';
import { ApiResponses } from '../../../../core/models/common.models';

@Injectable({
  providedIn: 'root',
})
export class InsightsDataService {
  private readonly API_URL = `${environment.apiUrl}/BehaviorData`;
  constructor(
    private errorHandler: ErrorHandlingService,
    private http: HttpClient
  ) {}

  getInsightData(id: number) {
    return this.http
      .get<ApiResponse<InsightData>>(`${this.API_URL}/status/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  enableDatasheet(payload: enableDatasheet): Observable<ApiResponse<void>> {
    return this.http
      .post<ApiResponse<void>>(`${this.API_URL}`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  removeDatasheet(payload: enableDatasheet): Observable<ApiResponse<void>> {
    return this.http
      .delete<
        ApiResponse<void>
      >(`${this.API_URL}?caseBehaviorId=${payload.caseBehaviorId}&unitName=${payload.unitName}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  private buildQueryParams({
    CaseBehaviorId,
    UnitName,
    StartDate,
    EndDate,
    graphType,
    viewType,
  }: DataSheetQueryParams): HttpParams {
    let params = new HttpParams();

    if (CaseBehaviorId !== null && CaseBehaviorId !== undefined) {
      params = params.set('CaseBehaviorId', CaseBehaviorId.toString());
    }
    if (UnitName !== null && UnitName !== undefined) {
      params = params.set('UnitName', UnitName.toString());
    }

    if (StartDate) {
      params = params.set('StartDate', StartDate);
    }
    if (EndDate) {
      params = params.set('EndDate', EndDate);
    }
    if (viewType !== null && viewType !== undefined) {
      params = params.set('viewType', viewType.toString());
    }
    if (graphType !== null && graphType !== undefined) {
      params = params.set('graphType', graphType.toString());
    }
    return params;
  }

  getDataSheetData(
    queryParams: DataSheetQueryParams
  ): Observable<ApiResponse<DataSheetResult>> {
    //implement user get
    const params = this.buildQueryParams(queryParams);
    return this.http
      .get<ApiResponse<DataSheetResult>>(`${this.API_URL}`, {
        params,
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getGraphData(
    queryParams: DataSheetQueryParams
  ): Observable<ApiResponse<DataSheetResult>> {
    //implement user get
    const params = this.buildQueryParams(queryParams);
    return this.http
      .get<ApiResponse<DataSheetResult>>(`${this.API_URL}/graph-data`, {
        params,
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getBehaviorGraphData(
    queryParams: DataSheetQueryParams
  ): Observable<ApiResponse<BehaviorGraphDataResponse>> {
    const params = this.buildQueryParams(queryParams);
    return this.http
      .get<ApiResponse<BehaviorGraphDataResponse>>(
        `${this.API_URL}/behaviour-graph-data`,
        {
          params,
        }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getMultipleGraphData(
    params: BIPGraphPayload
  ): Observable<ApiResponse<BehaviorMultiGraphDataResponse>> {
    return this.http
      .post<
        ApiResponse<BehaviorMultiGraphDataResponse>
      >(`${this.API_URL}/behaviour-graph-data-batch`, params)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getGraphList(caseId: number): Observable<ApiResponses<GraphList>> {
    //implement user get
    return this.http
      .get<
        ApiResponses<GraphList>
      >(`${this.API_URL}/graph-list?caseId=${caseId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  saveBlockValue(
    payload: BlockValueModel
  ): Observable<ApiResponse<DataSheetResult>> {
    return this.http
      .put<ApiResponse<DataSheetResult>>(`${this.API_URL}/block-value`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  saveMultiValue(payload: Unit): Observable<ApiResponse<DataSheetResult>> {
    return this.http
      .put<ApiResponse<DataSheetResult>>(`${this.API_URL}/multi-value`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteUnitValue(id: number, date: string) {
    return this.http
      .delete<
        ApiResponse<void>
      >(`${this.API_URL}/unit-value?unitId=${id}&dataDate=${date}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  generateTimeSlots(
    data: UnitMultiValue[]
  ): { startTime: string; endTime: string }[] {
    return data
      .filter(item => item.startDataTime && item.endDataTime) // Ensure values exist
      .map(item => ({
        startTime: item.startDataTime?.slice(0, 5) || '', // Default to empty string if undefined
        endTime: item.endDataTime?.slice(0, 5) || '',
      }));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  convertImagetoBase64(chartInstance: any): string {
    const chartImageBase64 = chartInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });
    return chartImageBase64;
  }

  adjustDates(
    startDate: Date,
    endDate: Date
  ): { adjustedStartDate: Date; adjustedEndDate: Date } {
    const adjustedStartDate = new Date(startDate);
    const adjustedEndDate = new Date(endDate);

    // Adjust start date to the previous Monday if not Monday
    if (adjustedStartDate.getDay() !== 1) {
      // Monday is 1
      adjustedStartDate.setDate(
        adjustedStartDate.getDate() - ((adjustedStartDate.getDay() + 6) % 7)
      );
    }

    // Adjust end date based on conditions
    if (adjustedEndDate.getDay() === 6) {
      // Saturday
      adjustedEndDate.setDate(adjustedEndDate.getDate() - 1); // Move to previous Friday
    } else if (adjustedEndDate.getDay() === 0) {
      // Sunday
      adjustedEndDate.setDate(adjustedEndDate.getDate() - 2); // Move to previous Friday
    } else if (adjustedEndDate.getDay() !== 5) {
      // If not Friday
      adjustedEndDate.setDate(
        adjustedEndDate.getDate() + (5 - adjustedEndDate.getDay())
      ); // Move to next Friday
    }

    return { adjustedStartDate, adjustedEndDate };
  }

  getComments(
    unitId: number,
    date: string
  ): Observable<ApiResponse<DataNote[]>> {
    return this.http
      .get<
        ApiResponse<DataNote[]>
      >(`${this.API_URL}/comment?activityDataUnitId=${unitId}&dataDate=${date}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  addComment(payload: AddComment): Observable<ApiResponse<AddComment>> {
    return this.http
      .post<ApiResponse<AddComment>>(`${this.API_URL}/comment`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  editComment(payload: EditComment): Observable<ApiResponse<EditComment>> {
    return this.http
      .put<ApiResponse<EditComment>>(`${this.API_URL}/comment`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteComment(id: number): Observable<ApiResponse<boolean>> {
    return this.http
      .delete<ApiResponse<boolean>>(`${this.API_URL}/comment?id=${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getRandomColor() {
    // Curated graph theme colors (excluding green and yellow shades)
    const themeColors = [
      '#FF6384', // Red
      '#36A2EB', // Blue
      '#9966FF', // Purple
      '#FF9F40', // Orange
      '#C9CBCF', // Grey
      '#D4A6C8', // Pink
      '#FFA07A', // Light Salmon
      '#20B2AA', // Light Sea Green
      '#9370DB', // Medium Purple
      '#A0522D', // Sienna
      '#8B4513', // Saddle Brown
      '#DC143C', // Crimson
      '#800080', // Purple
      '#4682B4', // Steel Blue
      '#B22222', // Firebrick
    ];
    const idx = Math.floor(Math.random() * themeColors.length);
    return themeColors[idx];
  }
  calculateTrendline(x: number[], y: (number | null)[]): (number | null)[] {
    // Collect only valid data points
    const points = x
      .map((xi, i) => ({ x: xi, y: y[i] }))
      .filter(p => p.y !== null) as { x: number; y: number }[];

    // Not enough points for regression
    if (points.length < 2) {
      return y.map(() => null);
    }

    const n = points.length;

    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    for (const p of points) {
      sumX += p.x;
      sumY += p.y;
      sumXY += p.x * p.y;
      sumX2 += p.x * p.x;
    }

    const denominator = n * sumX2 - sumX * sumX;
    if (denominator === 0) {
      return y.map(() => null);
    }

    const m = (n * sumXY - sumX * sumY) / denominator;
    const b = (sumY - m * sumX) / n;

    // Generate trendline values
    return x.map((xi, i) => (y[i] === null ? null : m * xi + b));
  }

  generateTrendlineArray(
    xLabels: string[],
    yValues: (number | null | undefined)[]
  ): (number | null)[] {
    if (!xLabels?.length || !yValues?.length) return [];

    const x = xLabels.map((_, i) => i);
    const sanitizedY: (number | null)[] = yValues.map(v =>
      v === undefined ? null : v
    );

    return this.calculateTrendline(x, sanitizedY);
  }
}
